import React, { useState } from 'react';
import { scopeNames, toggleOptionalScope } from './permissions';

function Chevron({ className }: { className: string }) {
    return (
        <svg
            className={className}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d="m6 9 6 6 6-6" />
        </svg>
    );
}

type Store = { id: string; name: string; storeName: string };
const groups = [
    { id: 'themes', title: 'Storefront themes', description: 'Read and customize private theme drafts. Publishing changes the live storefront.', scopes: ['themes:read','themes:write','themes:publish'] },
    {
        id: 'catalogue',
        title: 'Products & pricing',
        description: 'Catalogue details, variants, product images and prices.',
        scopes: [
            'products:read',
            'categories:read',
            'categories:write',
            'products:write',
            'products:create',
            'products:variants',
            'products:variant_edit',
            'products:images',
            'catalogue_pricing:write',
            'pricing:read',
            'pricing:write',
        ],
    },
    {
        id: 'stock',
        title: 'Stock & outlets',
        description: 'Stock levels and product availability across your outlets.',
        scopes: [
            'inventory:read',
            'inventory:write',
            'product_outlets:read',
            'product_outlets:write',
        ],
    },
    {
        id: 'orders',
        title: 'Orders',
        description:
            'View orders, accept them and manage fulfillment when allowed. COD completion records cash collection.',
        scopes: ['orders:read', 'orders:accept', 'orders:fulfill'],
    },
    {
        id: 'content',
        title: 'Content & media',
        description: 'FAQs, collections, navigation menus, announcements, image uploads and store covers.',
        scopes: [
            'announcements:read',
            'announcements:write',
            'faqs:read',
            'faqs:write',
            'collections:read',
            'collections:write',
            'navigation:read',
            'navigation:write',
            'images:read',
            'images:write',
        ],
    },
    {
        id: 'delivery',
        title: 'Delivery & pickup',
        description: 'Delivery and pickup slots, outlet assignments and service coverage.',
        scopes: ['slots:read', 'slots:write', 'delivery_areas:read', 'delivery_areas:write'],
    },
    {
        id: 'settings',
        title: 'Store settings',
        description: 'Store profile, discovery, opening hours and temporary closures.',
        scopes: [
            'store_profile:read',
            'store_profile:write',
            'store_operations:read',
            'store_operations:write',
        ],
    },
    {
        id: 'customers',
        title: 'Customers',
        description: 'Customer contact details and order history for the selected stores.',
        scopes: ['customers:read'],
    },
    {
        id: 'promotions',
        title: 'Promotions & charges',
        description: 'Discount rules and delivery or additional fees charged at checkout.',
        scopes: ['promotions:read', 'promotions:write', 'additional_charges:read', 'additional_charges:write'],
    },
    {
        id: 'insights',
        title: 'Sales insights',
        description: 'Sales metrics, top products and outlet performance.',
        scopes: ['insights:read'],
    },
];
const isRead = (scope: string) => scope.endsWith(':read');
const basics = ['stores:read', 'offline_access'];
const summaries: Record<string, string> = {
    'orders:fulfill': 'Manage fulfillment & COD collection',
    'customers:read': 'View store customers & order history',
    'categories:read': 'View categories',
    'categories:write': 'Manage categories & product assignments',
    'promotions:read': 'View promotions',
    'promotions:write': 'Manage promotions',
    'additional_charges:read': 'View charges',
    'additional_charges:write': 'Manage checkout charges',
    'announcements:read': 'View announcements',
    'announcements:write': 'Manage & publish announcements',
    'products:read': 'View products',
    'products:write': 'Edit text & SEO',
    'products:create': 'Create products',
    'products:variants': 'Add variants',
    'products:variant_edit': 'Edit & retire variants',
    'products:images': 'Product images',
    'catalogue_pricing:write': 'Base prices',
    'pricing:read': 'View outlet prices',
    'themes:read': 'View themes & previews',
    'themes:write': 'Edit theme drafts',
    'themes:publish': 'Publish & roll back themes',
    'pricing:write': 'Edit outlet prices',
    'inventory:read': 'View stock',
    'inventory:write': 'Adjust stock',
    'product_outlets:read': 'View assignments',
    'product_outlets:write': 'Assign & remove products',
    'orders:read': 'Orders & customer details',
    'orders:accept': 'Accept pending orders',
    'faqs:read': 'View FAQs',
    'faqs:write': 'Manage FAQs',
    'collections:read': 'View collections',
    'collections:write': 'Manage collections',
    'images:read': 'View images',
    'images:write': 'Upload images & edit covers',
    'navigation:read': 'View navigation menus',
    'navigation:write': 'Manage navigation menus',
    'slots:read': 'View slots',
    'slots:write': 'Manage slots',
    'delivery_areas:read': 'View coverage',
    'delivery_areas:write': 'Edit coverage',
    'store_profile:read': 'View profile',
    'store_profile:write': 'Edit profile & discovery',
    'store_operations:read': 'View settings',
    'store_operations:write': 'Hours & temporary closures',
    'insights:read': 'View sales performance',
};

export function StoreSelection({
    stores,
    selected,
    onSelected,
    includeFutureStores,
    onFutureStores,
    supportsFutureStores,
    busy = false,
    initiallyOpen = false,
}: {
    stores: Store[];
    selected: string[];
    onSelected: (ids: string[]) => void;
    includeFutureStores: boolean;
    onFutureStores: (value: boolean) => void;
    supportsFutureStores: boolean;
    busy?: boolean;
    initiallyOpen?: boolean;
}) {
    const [open, setOpen] = useState(initiallyOpen);
    const [query, setQuery] = useState('');
    const all = stores.length > 0 && selected.length === stores.length;
    const visible = stores.filter((store) =>
        `${store.name} ${store.storeName}`.toLowerCase().includes(query.toLowerCase().trim()),
    );
    return (
        <div className="oauth-store-choice">
            <div className="oauth-section-title">
                <div>
                    <span className="oauth-eyebrow">STORE ACCESS</span>
                    <h2>{stores.length === 1 ? 'Your store' : 'Your stores'}</h2>
                </div>
                {stores.length > 1 && (
                    <button
                        type="button"
                        className="oauth-text-button"
                        disabled={busy}
                        aria-expanded={open}
                        aria-controls="oauth-store-options"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? 'Done' : 'Change selection'}
                    </button>
                )}
            </div>
            {!stores.length ? (
                <p>
                    No stores are eligible for these permissions. Check your merchant role and
                    subscription.
                </p>
            ) : (
                <>
                    <p id="store-selection-summary" className="oauth-store-summary" role="status">
                        {stores.length === 1
                            ? stores[0].name
                            : all
                              ? `All ${stores.length} stores selected`
                              : `${selected.length} of ${stores.length} stores selected`}
                    </p>
                    {stores.length === 1 && (
                        <span className="oauth-muted">{stores[0].storeName}</span>
                    )}
                    {open && stores.length > 1 && (
                        <div id="oauth-store-options" className="oauth-store-options">
                            {stores.length > 5 && (
                                <>
                                    <label htmlFor="oauth-store-search">Find a store</label>
                                    <input
                                        id="oauth-store-search"
                                        type="search"
                                        value={query}
                                        onChange={(event) => setQuery(event.target.value)}
                                        placeholder="Search stores"
                                        disabled={busy}
                                    />
                                </>
                            )}
                            <div className="oauth-store-toolbar">
                                <button
                                    type="button"
                                    className="secondary"
                                    disabled={busy}
                                    onClick={() => onSelected(stores.map((store) => store.id))}
                                >
                                    Select all stores
                                </button>
                                <button
                                    type="button"
                                    className="secondary"
                                    disabled={busy}
                                    onClick={() => onSelected([])}
                                >
                                    Clear selection
                                </button>
                            </div>
                            <div className="oauth-store-list">
                                {visible.map((store) => (
                                    <label className="oauth-store" key={store.id}>
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(store.id)}
                                            disabled={busy}
                                            onChange={(event) =>
                                                onSelected(
                                                    event.target.checked
                                                        ? [...selected, store.id]
                                                        : selected.filter((id) => id !== store.id),
                                                )
                                            }
                                        />
                                        <span className="oauth-store-name">
                                            <strong>{store.name}</strong>
                                            <small>{store.storeName}</small>
                                        </span>
                                    </label>
                                ))}
                                {!visible.length && <p>No stores match your search.</p>}
                            </div>
                        </div>
                    )}
                    {supportsFutureStores && (
                        <label className="oauth-future-choice">
                            <input
                                type="checkbox"
                                data-future-stores
                                checked={includeFutureStores}
                                disabled={busy}
                                onChange={(event) => onFutureStores(event.target.checked)}
                            />
                            <span>
                                <strong>Automatically include new stores</strong>
                                <small>
                                    Newly eligible stores get the same approved permissions. Stores
                                    you exclude stay excluded.
                                </small>
                            </span>
                        </label>
                    )}
                    {!selected.length && (
                        <p className="oauth-selection-warning" role="status">
                            Choose at least one store to continue.
                        </p>
                    )}
                    {selected.length > 0 &&
                        selected.length < stores.length &&
                        includeFutureStores && (
                            <p className="oauth-muted">
                                {stores.length - selected.length} current{' '}
                                {stores.length - selected.length === 1 ? 'store is' : 'stores are'}{' '}
                                excluded. Future eligible stores will be included.
                            </p>
                        )}
                </>
            )}
        </div>
    );
}

function PermissionGroups({
    required,
    optional,
    selected,
    onChange,
    busy,
}: {
    required: string[];
    optional: string[];
    selected: string[];
    onChange: (scopes: string[]) => void;
    busy: boolean;
}) {
    const offered = [...new Set([...required, ...optional])];
    const known = [...basics, ...groups.flatMap((group) => group.scopes)];
    const unknown = offered.filter((scope) => !known.includes(scope));
    const availableGroups = [
        ...groups,
        ...(unknown.length
            ? [
                  {
                      id: 'additional',
                      title: 'Additional access',
                      description:
                          'Other permissions requested by this app. Review each permission below.',
                      scopes: unknown,
                  },
              ]
            : []),
    ];
    const enabled = (scope: string) => required.includes(scope) || selected.includes(scope);
    function choose(scopes: string[], level: string) {
        let next = selected;
        // Removing read permissions also removes dependent writes in other groups.
        for (const scope of scopes.filter((scope) => optional.includes(scope)))
            next = toggleOptionalScope(next, scope, false, required);
        for (const scope of scopes.filter(
            (scope) =>
                optional.includes(scope) &&
                (level === 'manage' || (level === 'view' && isRead(scope))),
        ))
            next = toggleOptionalScope(next, scope, true, required);
        // Never manufacture a scope the authorization request did not offer.
        onChange(next.filter((scope) => optional.includes(scope)));
    }
    return (
        <div className="oauth-permission-groups">
            {availableGroups.map((group) => {
                const scopes = group.scopes.filter((scope) => offered.includes(scope));
                if (!scopes.length) return null;
                const active = scopes.filter(enabled);
                const reads = scopes.filter(isRead);
                const hasWrites = scopes.some((scope) => !isRead(scope));
                const level = !active.length
                    ? 'off'
                    : active.length === scopes.length
                      ? hasWrites
                          ? 'manage'
                          : 'view'
                      : active.length === reads.length && reads.every(enabled)
                        ? 'view'
                        : 'custom';
                const selectedSummary = active.length
                    ? active
                          .slice(0, 3)
                          .map((scope) => summaries[scope] || scopeNames[scope] || scope)
                          .join(' · ') + (active.length > 3 ? ` · +${active.length - 3} more` : '')
                    : 'This app will not have access to this area.';
                const manageLabel = group.id === 'orders'
                    ? scopes.includes('orders:fulfill') ? 'Manage fulfillment' : 'View & accept'
                    : 'Manage';
                const label =
                    level === 'off'
                        ? 'No access'
                        : level === 'view'
                          ? 'View'
                          : level === 'manage'
                            ? manageLabel
                            : 'Custom';
                return (
                    <details
                        key={group.id}
                        className="oauth-permission-group"
                        data-permission-group={group.id}
                    >
                        <summary>
                            <span className="oauth-group-symbol" aria-hidden="true">
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                >
                                    <rect x="4" y="4" width="16" height="16" rx="4" />
                                    <path d="m8 12 3 3 5-6" />
                                </svg>
                            </span>
                            <span className="oauth-group-title">
                                <strong>{group.title}</strong>
                                <small>{selectedSummary}</small>
                            </span>
                            <span
                                className={`oauth-access-badge ${level === 'off' ? 'is-off' : ''}`}
                            >
                                {label}
                            </span>
                            <Chevron className="oauth-disclosure-arrow" />
                        </summary>
                        <div className="oauth-group-content">
                            <label className="oauth-group-level">
                                Access level
                                <span className="oauth-select-control">
                                    <select
                                        aria-label={`${group.title} access level`}
                                        disabled={
                                            busy ||
                                            !scopes.some((scope) => optional.includes(scope))
                                        }
                                        value={level}
                                        onChange={(event) => choose(scopes, event.target.value)}
                                    >
                                        <option
                                            value="off"
                                            disabled={scopes.some((scope) =>
                                                required.includes(scope),
                                            )}
                                        >
                                            No access
                                        </option>
                                        {reads.length > 0 && (
                                            <option
                                                value="view"
                                                disabled={scopes.some(
                                                    (scope) =>
                                                        required.includes(scope) && !isRead(scope),
                                                )}
                                            >
                                                View
                                            </option>
                                        )}
                                        {hasWrites && (
                                            <option value="manage">
                                                {manageLabel}
                                            </option>
                                        )}
                                        <option value="custom" disabled>
                                            Custom
                                        </option>
                                    </select>
                                    <Chevron className="oauth-select-arrow" />
                                </span>
                            </label>
                            <p className="oauth-muted">
                                {group.id === 'orders'
                                    ? scopes.includes('orders:fulfill')
                                        ? 'Order access includes customer contact and fulfillment details. Fulfillment can notify customers. Completing COD orders confirms cash collection; online cancellation and refunds stay in the merchant admin app.'
                                        : 'Order access may include customer names, addresses and contact details. Acceptance can notify customers.'
                                    : 'Choose individual permissions below. Required permissions are fixed by this app; related read access is included automatically.'}
                            </p>
                            {scopes.map((scope) => (
                                <label className="oauth-scope-choice" key={scope}>
                                    <input
                                        type="checkbox"
                                        data-scope={scope}
                                        checked={enabled(scope)}
                                        disabled={busy || required.includes(scope)}
                                        onChange={(event) =>
                                            onChange(
                                                toggleOptionalScope(
                                                    selected,
                                                    scope,
                                                    event.target.checked,
                                                    required,
                                                ).filter((item) => optional.includes(item)),
                                            )
                                        }
                                    />
                                    <span>
                                        {scopeNames[scope] || scope}
                                        {required.includes(scope) && (
                                            <small>Required by this app</small>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </details>
                );
            })}
        </div>
    );
}

export default function OAuthConsent({
    request,
    stores,
    selected,
    onSelected,
    includeFutureStores,
    onFutureStores,
    additionalScopes,
    onScopes,
    busy,
    operation,
    onApprove,
    onCancel,
}: {
    request: any;
    stores: Store[];
    selected: string[];
    onSelected: (ids: string[]) => void;
    includeFutureStores: boolean;
    onFutureStores: (value: boolean) => void;
    additionalScopes: string[];
    onScopes: (scopes: string[]) => void;
    busy: boolean;
    operation: string;
    onApprove: () => void;
    onCancel: () => void;
}) {
    const scopes = [...request.scopes, ...additionalScopes];
    const count = groups.filter((group) =>
        group.scopes.some((scope) => scopes.includes(scope)),
    ).length;
    return (
        <section className="oauth-consent oauth-simplified" aria-label="Consent">
            <div className="oauth-consent-grid">
                <aside className="oauth-store-section">
                    <StoreSelection
                        stores={stores}
                        selected={selected}
                        onSelected={onSelected}
                        includeFutureStores={includeFutureStores}
                        onFutureStores={onFutureStores}
                        supportsFutureStores={request.supportsFutureStores}
                        busy={busy}
                    />
                    <p className="oauth-trust-note">
                        You can change store access or disconnect this app from Connected apps.
                    </p>
                </aside>
                <div className="oauth-access-section">
                    <span className="oauth-eyebrow">APP ACCESS</span>
                    <h2>What this app can do</h2>
                    <p className="oauth-access-intro">
                        Permissions are grouped by area. Open a group to customize access.
                    </p>
                    <PermissionGroups
                        required={request.scopes}
                        optional={request.optionalScopes || []}
                        selected={additionalScopes}
                        onChange={onScopes}
                        busy={busy}
                    />
                    <div className="oauth-connection-facts">
                        {scopes.includes('stores:read') && (
                            <p>Store information is available for the stores you authorize.</p>
                        )}
                        {scopes.includes('offline_access') && (
                            <p>
                                <strong>Stays connected while you’re away.</strong> This app can
                                continue using the access you approve without another sign-in.
                            </p>
                        )}
                        <p>Your current merchant role and Shopletzy’s action limits still apply.</p>
                    </div>
                </div>
            </div>
            <div className="oauth-approval-bar">
                <div className="oauth-approval-summary" role="status">
                    <strong>
                        {selected.length} {selected.length === 1 ? 'store' : 'stores'} · {count}{' '}
                        {count === 1 ? 'area' : 'areas'} enabled
                    </strong>
                    <span>
                        {includeFutureStores
                            ? 'Future eligible stores included'
                            : 'Only selected stores; new stores are not included'}
                    </span>
                </div>
                <div className="oauth-actions oauth-consent-actions">
                    <button disabled={busy || !selected.length} onClick={onApprove}>
                        {busy ? operation : `Connect ${request.clientName}`}
                    </button>
                    <button className="secondary" disabled={busy} onClick={onCancel}>
                        Cancel connection
                    </button>
                </div>
            </div>
        </section>
    );
}

export function StoreAccessEditor({
    connection,
    supportsFutureStores,
    busy,
    onSave,
}: {
    connection: any;
    supportsFutureStores: boolean;
    busy: boolean;
    onSave: (ids: string[], future: boolean) => void;
}) {
    const stores: Store[] = connection.availableStores;
    const [selected, setSelected] = useState<string[]>(
        connection.store_ids.filter((id: string) => stores.some((store) => store.id === id)),
    );
    const [future, setFuture] = useState(connection.include_future_stores === true);
    return (
        <div className="oauth-store-access-editor">
            <StoreSelection
                stores={stores}
                selected={selected}
                onSelected={setSelected}
                includeFutureStores={future}
                onFutureStores={setFuture}
                supportsFutureStores={supportsFutureStores}
                busy={busy}
                initiallyOpen
            />
            {connection.store_ids.some(
                (id: string) => !stores.some((store) => store.id === id),
            ) && (
                <p className="oauth-muted">
                    Unavailable stores will be removed from the saved selection.
                </p>
            )}
            <p className="oauth-muted">
                This changes store access only. Approved permissions stay the same.
            </p>
            <button disabled={busy || !selected.length} onClick={() => onSave(selected, future)}>
                Save store access
            </button>
        </div>
    );
}
